import React, { useState, useRef, useEffect, useCallback } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import { Modal, Input, Space, Select, Dropdown, Menu } from "antd";
import { DownOutlined } from "@ant-design/icons";

const { Option } = Select;

export default function EditVoice({ document, onClose, onUpdate, chatbotId, permission }) {
  const [pageContent, setPageContent] = useState(
    document?.content || "<p>Hello World!</p>"
  );
  const [allData, setAllData] = useState(document || "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [toolbarSaveStatus, setToolbarSaveStatus] = useState("save");
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);

  const editor = useEditor({
    extensions: [StarterKit, Table, TableCell, TableHeader, TableRow],
    content: pageContent,
    onUpdate: ({ editor }) => {
      setPageContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "tiptap-editor-area",
      },
    },
  });

  useEffect(() => {
    if (document?.content && editor) {
      setPageContent(document.content);
      editor.commands.setContent(document.content, false);
    }
  }, [document?.content, editor]);

  const handleSave = async () => {
    setIsSaving(true);
    setToolbarSaveStatus("saving");
    setError("");
    setSuccess("");

    try {
      const contentToSave = editor.getHTML();
      const params = chatbotId ? { chatbot_id: chatbotId } : {};

      const response = await axiosInstance.post(
        `/update-voice`,
        { id: document.id, content: contentToSave, all_data: allData },
        { params }
      );

      if (response.status === 200) {
        console.log("Voice data updated successfully");
        setSuccess("Voice data updated successfully!");
        setToolbarSaveStatus("saved");
        setTimeout(() => {
          onUpdate();
          setToolbarSaveStatus("save");
        }, 1500);
      }
    } catch (error) {
      console.error("Error updating voice data:", error);
      setError("Failed to update the voice data");
      setToolbarSaveStatus("save");
    } finally {
      setIsSaving(false);
    }
  };

  const insertTableDialog = useCallback(() => {
    setIsTableDialogOpen(true);
  }, [setIsTableDialogOpen]);

  const handleInsertTable = useCallback(() => {
    editor
      ?.chain()
      .focus()
      .insertTable({
        rows: parseInt(tableRows, 10),
        cols: parseInt(tableColumns, 10),
      })
      .run();
    setIsTableDialogOpen(false);
  }, [editor, tableRows, tableColumns, setIsTableDialogOpen]);

  const closeTableDialog = useCallback(() => {
    setIsTableDialogOpen(false);
  }, [setIsTableDialogOpen]);

  const handleTableRowsChange = useCallback(
    (e) => {
      setTableRows(e.target.value);
    },
    [setTableRows]
  );

  const handleTableColumnsChange = useCallback(
    (e) => {
      setTableColumns(e.target.value);
    },
    [setTableColumns]
  );

  const handleHeadingChange = useCallback(
    (value) => {
      editor.chain().focus().setParagraph().run();
      if (value !== "paragraph") {
        editor
          .chain()
          .focus()
          .toggleHeading({ level: parseInt(value.slice(1), 10) })
          .run();
      }
    },
    [editor]
  );

  const handleAddColumn = useCallback(() => {
    editor?.chain().focus().addColumnAfter().run();
  }, [editor]);

  const handleAddRow = useCallback(() => {
    editor?.chain().focus().addRowAfter().run();
  }, [editor]);

  const handleDeleteColumn = useCallback(() => {
    editor?.chain().focus().deleteColumn().run();
  }, [editor]);

  const handleDeleteRow = useCallback(() => {
    editor?.chain().focus().deleteRow().run();
  }, [editor]);

  const handleDeleteTable = useCallback(() => {
    editor?.chain().focus().deleteTable().run();
  }, [editor]);

  if (!editor) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const tableMenu = (
    <Menu>
      <Menu.Item key="insertTable" onClick={insertTableDialog}>
        Insert Table
      </Menu.Item>
      <Menu.Item
        key="addColumn"
        onClick={handleAddColumn}
        disabled={!editor.isActive("table")}
      >
        Add Column
      </Menu.Item>
      <Menu.Item
        key="addRow"
        onClick={handleAddRow}
        disabled={!editor.isActive("table")}
      >
        Add Row
      </Menu.Item>
      <Menu.Item
        key="deleteColumn"
        onClick={handleDeleteColumn}
        disabled={!editor.isActive("table")}
      >
        Delete Column
      </Menu.Item>
      <Menu.Item
        key="deleteRow"
        onClick={handleDeleteRow}
        disabled={!editor.isActive("table")}
      >
        Delete Row
      </Menu.Item>
      <Menu.Item
        key="deleteTable"
        onClick={handleDeleteTable}
        disabled={!editor.isActive("table")}
      >
        Delete Table
      </Menu.Item>
    </Menu>
  );

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={700} color="textPrimary">
          Edit Voice Data
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ maxHeight: "calc(90vh - 200px)", overflow: "auto" }}>
        <div className="sticky-toolbar">
          <div className="toolbar">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Bold"
            >
              <i className="fa fa-bold"></i>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Italic"
            >
              <i className="fa fa-italic"></i>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              title="Underline"
            >
              <i className="fa fa-underline"></i>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleStrike().run()}
              title="Strike Through"
            >
              <i className="fa fa-strikethrough"></i>
            </button>

            <Select
              defaultValue="paragraph"
              style={{ width: 120 }}
              onChange={handleHeadingChange}
            >
              <Option value="paragraph">Paragraph</Option>
              <Option value="h1">Heading 1</Option>
              <Option value="h2">Heading 2</Option>
              <Option value="h3">Heading 3</Option>
              <Option value="h4">Heading 4</Option>
              <Option value="h5">Heading 5</Option>
              <Option value="h6">Heading 6</Option>
            </Select>

            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              title="Bullet List"
            >
              <i className="fa fa-list-ul"></i>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              title="Ordered List"
            >
              <i className="fa fa-list-ol"></i>
            </button>

            <Dropdown
              overlay={tableMenu}
              trigger={["click"]}
              overlayStyle={{ zIndex: 1500 }}
            >
              <button style={{ marginLeft: "8px", marginRight: "8px" }}>
                <i className="fa fa-table" style={{ marginRight: "4px" }}></i>{" "}
                Table <DownOutlined />
              </button>
            </Dropdown>

            <button
              onClick={() => editor.chain().focus().undo().run()}
              title="Undo"
            >
              <i className="fa fa-undo"></i>
            </button>
            <button
              onClick={() => editor.chain().focus().redo().run()}
              title="Redo"
            >
              <i className="fa fa-repeat"></i>
            </button>
            {permission.canModify && (
              <button onClick={handleSave} title="Save">
                {toolbarSaveStatus === "saving" ? (
                  <i className="fa fa-spinner fa-spin"></i>
                ) : toolbarSaveStatus === "saved" ? (
                  <i className="fa fa-check"></i>
                ) : (
                  <i className="fa fa-save"></i>
                )}
              </button>
            )}
          </div>
        </div>

        <EditorContent editor={editor} />
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        {permission.canModify && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            fullWidth
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        )}
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={isSaving}
          fullWidth
        >
          Cancel
        </Button>
      </Stack>

      {!permission?.canModify && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          You don't have permission to edit voice data.
        </Alert>
      )}

      {/* Insert Table Modal */}
      <Modal
        title="Insert Table"
        open={isTableDialogOpen}
        onOk={handleInsertTable}
        onCancel={closeTableDialog}
        okText="Insert"
        cancelText="Cancel"
        zIndex={1500}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input
            type="number"
            addonBefore="Rows"
            value={tableRows}
            onChange={handleTableRowsChange}
            min={1}
          />
          <Input
            type="number"
            addonBefore="Columns"
            value={tableColumns}
            onChange={handleTableColumnsChange}
            min={1}
          />
        </Space>
      </Modal>

      <style jsx global>{`
        .tiptap-editor-area {
          min-height: 200px;
          max-height: calc(90vh - 280px);
          overflow-y: auto;
          border: 1px solid #ced4da;
          border-radius: 0.25rem;
        }
        .tiptap-editor-area:focus-within,
        .tiptap-editor-area.ProseMirror-focused {
          border-color: #86b7fe;
          outline: 0;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        .tiptap-toolbar {
          flex-shrink: 0;
          z-index: 1;
          background-color: #f8f9fa;
        }
        .tiptap-invalid .tiptap-editor-area {
          border-color: #dc3545 !important;
        }
        .tiptap-invalid .tiptap-editor-area:focus-within,
        .tiptap-invalid .tiptap-editor-area.ProseMirror-focused {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
        }
        .ProseMirror {
          padding: 0.5rem;
        }
        .ProseMirror p {
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }
        .ProseMirror ul,
        .ProseMirror ol {
          padding-left: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .ProseMirror li {
          margin-bottom: 0.1rem;
        }
        .ProseMirror li p {
          margin-bottom: 0.1rem;
        }
        .ProseMirror a {
          color: #0d6efd;
          text-decoration: underline;
          cursor: pointer;
        }
        .ProseMirror h1,
        .ProseMirror h2,
        .ProseMirror h3,
        .ProseMirror h4,
        .ProseMirror h5,
        .ProseMirror h6 {
          margin-top: 0.75rem;
          margin-bottom: 0.3rem;
          line-height: 1.3;
          font-weight: 500;
        }
        .ProseMirror table {
          border-collapse: collapse;
          margin: 0.5rem 0;
          width: 100%;
        }
        .ProseMirror th,
        .ProseMirror td {
          border: 1px solid #dee2e6;
          padding: 0.3rem 0.4rem;
          vertical-align: top;
        }
        .ProseMirror th {
          font-weight: bold;
          text-align: left;
          background-color: #f1f3f5;
        }
        .tiptap-toolbar .ant-btn-sm {
          margin-bottom: 2px;
          margin-top: 2px;
        }
        .tiptap-toolbar .ant-select-sm {
          margin: 2px 8px 2px 8px !important;
          vertical-align: middle;
        }

        .ant-select-dropdown,
        .ant-dropdown-menu {
          z-index: 1400 !important;
        }
      `}</style>
    </Box>
  );
}